/**
 * API Service: Programs
 *
 * Encapsulates all HTTP calls for Programs using the centralized
 * Axios-based apiService. Follows the same pattern as TieringSettingsApiService.
 */

import { config } from "@/config";
import { apiService, type PaginatedApiResponse } from "@/remote/api";

import { mockProgramsData } from "../../../mock/programsMock";
import type {
  CreateProgramRequest,
  CreateProgramResponseDto,
  ProgramDetailDto,
  ProgramListItemDto,
  ProductPreviewDataDto,
  UpdateProgramRequest,
  UpdateProgramResponseDto,
  WhitelistPreviewDataDto,
} from "../response/ProgramsResponse";

export interface FetchProgramsParams {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: string;
  readonly search?: string;
}

export class ProgramsApiService {
  private readonly useMockData: boolean;

  constructor() {
    this.useMockData = config.features.useMockData;
  }

  /**
   * Fetch programs with pagination and optional filters.
   * Endpoint: GET /v1/programs
   */
  public async fetchProgramsPaginated(
    params: FetchProgramsParams = {},
  ): Promise<PaginatedApiResponse<ProgramListItemDto>> {
    const { page = 1, pageSize = 10, status, search } = params;

    if (this.useMockData) {
      let filtered = [...mockProgramsData.data];

      if (status && status !== "all") {
        filtered = filtered.filter((item) => item.status === status);
      }

      if (search) {
        const normalizedSearch = search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.programId.toLowerCase().includes(normalizedSearch) ||
            item.programName.toLowerCase().includes(normalizedSearch),
        );
      }

      const startIndex = (page - 1) * pageSize;
      const pageItems = filtered.slice(startIndex, startIndex + pageSize);

      const data: ProgramListItemDto[] = pageItems.map((item) => ({
        programId: item.programId,
        name: "Event Trigger",
        programName: item.programName,
        status: item.status,
        startDate: item.startDate,
        endDate: item.endDate,
        incentiveType: "product",
        basedOn: "revenue",
        totalProducts: 487,
        totalCustomers: 1247,
        createdAt: new Date().toISOString(),
        actions: {
          canEdit: true,
          canDelete: item.status !== "ongoing",
          canActivate: item.status === "not_started",
        },
      }));

      const totalRecords = filtered.length;
      const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

      return {
        data,
        paging: {
          currentPage: page,
          pageSize,
          totalPages,
          totalRecords,
        },
        meta: {
          code: "20000",
          message: "OK (mock programs)",
          timestamp: new Date().toISOString(),
        },
      };
    }

    return apiService.getPaginated<ProgramListItemDto>("/product/api/v1/programs", {
      params: {
        page,
        pageSize,
        status,
        search,
      },
    });
  }

  /**
   * Fetch detailed information for a specific program.
   * Endpoint: GET /v1/programs/:programId
   */
  public async fetchProgramDetail(programId: string): Promise<ProgramDetailDto> {
    if (this.useMockData) {
      const mockItem = mockProgramsData.data.find(
        (item) => item.id === programId || item.programId === programId,
      );

      return {
        program: {
          programId: mockItem?.programId ?? programId,
          name: "Event Trigger",
          programName: mockItem?.programName ?? "Mock Program",
          startDate: mockItem?.startDate ?? "2025-01-01",
          endDate: mockItem?.endDate ?? "2025-12-31",
          status: mockItem?.status ?? "draft",
        },
        incentiveRule: {
          type: "product",
          basedOn: "revenue",
          minimumTransaction: 500000,
          maximumTransaction: 10000000,
          totalProducts: 487,
        },
        customerSegment: {
          areas: ["all"],
          tiers: ["gold", "platinum"],
          whitelistCount: 1247,
        },
      };
    }

    const response = await apiService.get<ProgramDetailDto>(
      `/product/api/v1/programs/${programId}`,
    );

    return response.data;
  }

  /**
   * Create a new program.
   * Endpoint: POST /v1/programs
   */
  public async createProgram(
    payload: CreateProgramRequest,
  ): Promise<CreateProgramResponseDto> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        const generatedId = `prog-${Date.now().toString(36)}`;
        setTimeout(
          () =>
            resolve({
              programId: generatedId,
              message: "Program created successfully",
            }),
          300,
        );
      });
    }

    return apiService.postData<CreateProgramResponseDto, CreateProgramRequest>(
      "/product/api/v1/programs",
      payload,
    );
  }

  /**
   * Update an existing program.
   * Endpoint: PUT /v1/programs/:programId
   */
  public async updateProgram(
    programId: string,
    payload: UpdateProgramRequest,
  ): Promise<UpdateProgramResponseDto> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () => resolve({ message: "Program updated successfully" }),
          300,
        );
      });
    }

    return apiService.putData<UpdateProgramResponseDto, UpdateProgramRequest>(
      `/product/api/v1/programs/${programId}`,
      payload,
    );
  }

  /**
   * Delete a program.
   * Endpoint: DELETE /v1/programs/:programId
   */
  public async deleteProgram(programId: string): Promise<void> {
    if (this.useMockData) {
      return Promise.resolve();
    }

    await apiService.deleteData<void>(`/product/api/v1/programs/${programId}`);
  }

  /**
   * Upload whitelist file and get preview with temporary file ID.
   * Endpoint: POST /v1/programs/whitelist-preview
   */
  public async uploadWhitelistFile(
    file: File,
    page: number = 1,
    limit: number = 50,
  ): Promise<WhitelistPreviewDataDto> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              totalCustomers: 1247,
              preview: [
                { customerId: "CUST001234", name: "Budi Santoso", tier: "Platinum" },
                { customerId: "CUST001235", name: "Siti Rahayu", tier: "Gold" },
                { customerId: "CUST001236", name: "Ahmad Wijaya", tier: "Silver" },
              ],
              temporaryFileId: `tmp_cust_${Date.now().toString(36)}`,
            }),
          500,
        );
      });
    }

    const formData = new FormData();
    formData.append("whitelistFile", file);

    return apiService.postData<WhitelistPreviewDataDto, FormData>(
      `/product/api/v1/programs/whitelist-preview?page=${page}&limit=${limit}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  }

  /**
   * Upload product incentive file and get preview with temporary file ID.
   * Endpoint: POST /v1/programs/product-incentive-preview
   */
  public async uploadProductFile(
    file: File,
    page: number = 1,
    limit: number = 50,
  ): Promise<ProductPreviewDataDto> {
    if (this.useMockData) {
      return new Promise((resolve) => {
        setTimeout(
          () =>
            resolve({
              totalProducts: 487,
              preview: [
                { no: 1, productId: "GFD7R5624B14B14019", productName: "Matcha Latte", pointIncentive: 100 },
                { no: 2, productId: "GFD7R5624B14B14020", productName: "Espresso", pointIncentive: 80 },
                { no: 3, productId: "GFD7R5624B14B14021", productName: "Cappuccino", pointIncentive: 90 },
              ],
              temporaryFileId: `tmp_prod_${Date.now().toString(36)}`,
            }),
          500,
        );
      });
    }

    const formData = new FormData();
    formData.append("productFile", file);

    return apiService.postData<ProductPreviewDataDto, FormData>(
      `/product/api/v1/programs/product-incentive-preview?page=${page}&limit=${limit}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  }
}

